import { useState } from 'react';
import { Button, Box, Flex, Heading, Divider, Tooltip } from '@vibe/core';
import {
  Template,
  TemplateConfig,
  Sheet as SheetType,
  FieldGroup as FieldGroupType,
} from '@gorilla/spreadsheet-shared/src/lib/spreadsheet-manager/types';
import { FieldGroup } from './FieldGroup';
import { AddSmall, Board as BoardIcon } from '@vibe/icons';
import { Board } from '@gorilla/common/src/lib/monday-api/api';
import { getDefaultColumns } from '../hooks/use-spreadsheet-template';
import { BoardPickerModal } from './BoardPickerModal';
import { createRandomId } from '../lib/utils';

type SheetProps = {
  boards: Board[];

  sheet: SheetType;

  config: TemplateConfig;

  template: Template;

  onRemove: () => void;

  onSave: () => void;

  isRemoveable: boolean;

  onFieldGroupAdd: (fieldGroup: FieldGroupType) => void;

  onFieldGroupRemove: (fieldGroup: FieldGroupType) => void;

  onFieldGroupChange: (fieldGroup: FieldGroupType) => void;

  onNameChange: (name: string) => void;

  onOpenBoardManager: () => void;
};

export function Sheet({
  boards,
  sheet,
  config,
  template,
  onRemove,
  onSave,
  isRemoveable,
  onFieldGroupAdd,
  onFieldGroupRemove,
  onFieldGroupChange,
  onNameChange,
  onOpenBoardManager,
}: SheetProps) {
  const [showBoardPicker, setShowBoardPicker] = useState(false);

  return (
    <>
      {showBoardPicker ? (
        <BoardPickerModal
          show={showBoardPicker}
          onClose={async (boardId) => {
            setShowBoardPicker(false);

            if (!boardId) {
              return;
            }

            const columns = await getDefaultColumns(boardId);

            const newFieldGroup: FieldGroupType = {
              id: createRandomId(),
              board_id: boardId,
              show_headlines: false,
              columns,
            };

            onFieldGroupAdd(newFieldGroup);
          }}
        />
      ) : null}

      {sheet.field_groups.length ? (
        <>
          {sheet.field_groups.map((fieldGroup, i) => {
            return (
              <div key={fieldGroup.id}>
                {i !== 0 ? (
                  <>
                    <Divider withoutMargin />
                  </>
                ) : (
                  <></>
                )}

                <FieldGroup
                  boards={boards}
                  config={config}
                  sheet={sheet}
                  fieldGroup={fieldGroup}
                  template={template}
                  onChange={(changedFieldGroup) => {
                    onFieldGroupChange(changedFieldGroup);
                  }}
                  onRemove={() => {
                    onFieldGroupRemove(fieldGroup);
                  }}
                  onSave={onSave}
                />
              </div>
            );
          })}

          <Divider withoutMargin />

          <div
            style={{
              padding: '0px',
              backgroundColor: '#fff',
              borderBottomRightRadius: '8px',
              borderBottomLeftRadius: '8px',
            }}
          >
            <Flex gap={32} align="center" justify="start" direction="row">
              <Flex gap={0} align="center" justify="center" direction="row">
                <div
                  style={{
                    padding: '22px 22px 22px 24px',
                    minWidth: '80px',
                    borderRight: '1px solid #d0d4e4',
                    lineHeight: '1.3',
                  }}
                >
                  <Flex gap={16} align="center" justify="start" direction="row" style={{ color: 'rgb(175, 176, 189)' }}>
                    <div style={{ marginRight: 'auto' }}>
                      <strong>Worksheet</strong>
                      <br />
                      {sheet.name}
                    </div>
                  </Flex>
                </div>
                <div
                  style={{
                    padding: '22px 22px 22px 24px',
                    minWidth: '80px',
                    borderRight: '1px solid #d0d4e4',
                    lineHeight: '1.3',
                  }}
                >
                  <Flex gap={16} align="center" justify="start" direction="row" style={{ color: 'rgb(175, 176, 189)' }}>
                    <div style={{ marginRight: 'auto' }}>
                      <strong>Boards</strong>
                      <br />
                      {sheet.field_groups.length}
                    </div>
                  </Flex>
                </div>
                <div
                  style={{
                    padding: '22px 22px 22px 24px',
                    minWidth: '80px',
                    borderRight: '1px solid #d0d4e4',
                    lineHeight: '1.3',
                  }}
                >
                  <Flex gap={16} align="center" justify="start" direction="row" style={{ color: 'rgb(175, 176, 189)' }}>
                    <div style={{ marginRight: 'auto' }}>
                      <strong>Columns</strong>
                      <br />
                      {Math.max(...sheet.field_groups.map((fieldGroup) => fieldGroup.columns.length))}
                    </div>
                  </Flex>
                </div>
              </Flex>

              <div style={{ marginRight: '18px', marginLeft: 'auto' }}>
                <Flex gap={14} align="center" justify="center" direction="row">
                  <Tooltip content="Choose and append another board to this worksheet" position="left" zIndex={2000}>
                    <Button
                      leftIcon={AddSmall}
                      size="medium"
                      kind="primary"
                      onClick={async () => {
                        setShowBoardPicker(true);
                      }}
                    >
                      Append board
                    </Button>
                  </Tooltip>
                </Flex>
              </div>
            </Flex>
          </div>
        </>
      ) : (
        <>
          <Box margin="xl">
            <Flex align="center" justify="center" direction="row">
              <div
                style={{
                  marginTop: '80px',
                  marginBottom: '64px',
                  textAlign: 'center',
                }}
              >
                <Flex gap={10} align="center" justify="center" direction="column">
                  <Heading type="h2">Where did all the boards go?</Heading>
                  <p style={{ marginTop: '0px' }}>Give this worksheet some company and add a board.</p>
                  <Button
                    leftIcon={BoardIcon}
                    size="medium"
                    kind="primary"
                    onClick={() => {
                      setShowBoardPicker(true);
                    }}
                  >
                    Add board
                  </Button>
                </Flex>
              </div>
            </Flex>
          </Box>
        </>
      )}
    </>
  );
}
